import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LogIn, LogOut } from "lucide-react";
import { useAuth } from "../../auth/AuthContext.jsx";
import { useT, LanguageSwitcher } from "../../i18n/LanguageContext.jsx";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { t } = useT();

  const navItems = [
    { to: "/", label: t("nav.overview"), exact: true, testid: "nav-overview" },
    { to: "/simulator", label: t("nav.simulator"), testid: "nav-simulator", protected: true },
    { to: "/transactions", label: t("nav.transactions"), testid: "nav-transactions", protected: true },
    { to: "/accuracy", label: t("nav.accuracy"), testid: "nav-accuracy", protected: true },
    { to: "/config", label: t("nav.configuration"), testid: "nav-config", protected: true },
    { to: "/developers", label: t("nav.api"), testid: "nav-api-docs" },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="app-layout">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-6 pt-4 2xl:py-4 flex flex-wrap 2xl:flex-nowrap items-center justify-between gap-y-3">
          <div className="flex items-center gap-3 order-1 min-w-0 2xl:shrink-0">
            <img
              src="https://customer-assets.emergentagent.com/job_sepsis-index-api/artifacts/2wfrbxim_SIF_logo2.png"
              alt="Sepsi Index Framework"
              className="w-14 h-14 object-contain shrink-0"
              data-testid="app-logo"
            />
            <div className="leading-tight min-w-0">
              <div className="font-semibold text-slate-900 text-[15px] truncate">
                Sepsi Index Framework
              </div>
              <div className="data-label truncate">{t("layout.subtitle")}</div>
            </div>
          </div>
          <nav
            className="order-3 2xl:order-2 w-[calc(100%+3rem)] 2xl:w-auto -mx-6 px-6 2xl:mx-0 2xl:px-0 flex items-end 2xl:items-center gap-1 overflow-x-auto no-scrollbar border-t border-slate-100 2xl:border-0"
            data-testid="main-nav"
          >
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                data-testid={item.testid}
                className={({ isActive }) =>
                  [
                    "shrink-0 whitespace-nowrap text-sm font-medium transition-colors flex items-center gap-2",
                    "px-3 py-2.5 border-b-2 2xl:px-4 2xl:py-2 2xl:border",
                    isActive
                      ? "border-slate-900 text-slate-900 2xl:bg-slate-900 2xl:text-white"
                      : "border-transparent text-slate-600 hover:text-slate-900 2xl:text-slate-700 2xl:hover:bg-slate-100",
                  ].join(" ")
                }
              >
                <span>{item.label}</span>
                {item.protected && (
                  <svg className="hidden 2xl:block" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="order-2 2xl:order-3 2xl:ml-3 2xl:pl-3 2xl:border-l border-slate-200 flex items-center gap-2 shrink-0" data-testid="auth-box">
            <LanguageSwitcher />
            {user ? (
              <>
                <span className="hidden min-[1800px]:inline text-[12px] font-mono text-slate-600 max-w-[180px] truncate" title={user.email} data-testid="auth-email">
                  {user.email}
                </span>
                <button
                  onClick={logout}
                  title={user.email}
                  data-testid="btn-logout"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono border border-slate-300 hover:bg-slate-50"
                >
                  <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("layout.logout")}
                </button>
              </>
            ) : (
              <NavLink
                to="/login"
                data-testid="nav-login"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono border border-slate-300 hover:bg-slate-50"
              >
                <LogIn className="h-3.5 w-3.5" aria-hidden="true" />
                {t("layout.signIn")}
              </NavLink>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 py-5 flex flex-wrap items-center justify-between gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-3">
            <span className="data-label" data-testid="sponsor-footer-label">{t("layout.poweredBy")}</span>
            <img
              src="https://customer-assets.emergentagent.com/job_sepsis-index-api/artifacts/5vxfurvb_StarHealth_horizontal.jpeg"
              alt="StarHealth"
              className="h-7 w-auto object-contain opacity-80"
              data-testid="sponsor-footer"
            />
            <span className="font-mono pl-3 ml-1 border-l border-slate-200">{t("footer.source")}</span>
          </div>
          <div className="font-mono">{t("footer.tech")}</div>
        </div>
      </footer>
    </div>
  );
}
